/**
 * Wrappers tipados de queries y mutations de suscripción.
 *
 * Sprint 4.7: el flujo normal usa `family: 'pro' | 'premium'` — el backend
 * decide internamente si aplica Founder (si hay cupo) o Público.
 * Los códigos de invitación quedan como hook opcional para campañas VIP.
 */
import { useQuery, useMutation } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useUser } from '@/lib/auth';
import type { SubscriptionPlan } from '@gurumba/schemas';
import { conSid, metaCookies } from '@/lib/meta-pixel';

export interface PlanRow {
  code: SubscriptionPlan;
  display_name: string;
  description: string | null;
  price_usd_cents: number;
  interval: string;
  hierarchy_level: number;
  is_active: boolean;
  is_founder: boolean;
  max_seats: number | null;
  included_sessions_per_month: number;
  included_clinics_per_month: number;
  discount_on_sessions_pct: number;
}

/**
 * Hook: lista los planes activos. Por defecto, sólo planes PÚBLICOS no-founder
 * y no-free (los Free se desactivan durante beta, los Founder son resueltos
 * por backend en `resolve_active_plan`).
 */
export function usePlans(opts: { includeFounder?: boolean; includeFree?: boolean } = {}) {
  return useQuery({
    queryKey: ['subscription_plans', opts.includeFounder ?? false, opts.includeFree ?? false],
    queryFn: async (): Promise<PlanRow[]> => {
      let query = supabase
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true)
        .order('hierarchy_level', { ascending: true });

      if (!opts.includeFounder) {
        query = query.eq('is_founder', false);
      }

      const { data, error } = await query;
      if (error) throw error;
      let rows = data as PlanRow[];
      if (!opts.includeFree) {
        rows = rows.filter((p) => p.code !== 'free');
      }
      return rows;
    },
  });
}

/**
 * Hook: cupos disponibles de planes Founder. Usado por el banner en /pricing
 * para decidir si mostrar precio Founder o precio Público en cada card.
 */
export interface FounderSeatStatus {
  plan_code: SubscriptionPlan;
  max_seats: number;
  taken_seats: number;
  available_seats: number;
}

export function useFounderSeats() {
  return useQuery({
    queryKey: ['founder-seats'],
    queryFn: async (): Promise<Record<string, FounderSeatStatus>> => {
      const plans: SubscriptionPlan[] = ['pro_founder', 'premium_founder'];
      const results = await Promise.all(
        plans.map(async (p) => {
          const { data, error } = await supabase.rpc('available_founder_seats', { _plan_code: p });
          if (error) throw error;
          const row = (data?.[0] ?? { max_seats: 0, taken_seats: 0, available_seats: 0 }) as Omit<FounderSeatStatus, 'plan_code'>;
          return [p, { plan_code: p, ...row }] as const;
        })
      );
      return Object.fromEntries(results);
    },
    staleTime: 30_000, // refresca cada 30s para no saturar
  });
}

/**
 * Mutación: valida código Founder sin crear checkout.
 * Sólo se usa en campañas VIP — no en el flujo normal.
 */
export function useValidateFounderCode() {
  return useMutation({
    mutationFn: async (input: { code: string; plan: SubscriptionPlan }) => {
      const { data, error } = await supabase.rpc('validate_founder_code', {
        _code: input.code,
        _plan_code: input.plan,
      });
      if (error) throw new Error(error.message);
      return data?.[0] as { code_id: string; plan_code: string; available_seats: number };
    },
  });
}

/**
 * Hook: la suscripción activa del usuario actual.
 *
 * `pollWhileMissing` — útil post-checkout: cuando volvés del redirect de
 * Stripe a /dashboard?welcome=1, hay un gap de varios segundos antes de
 * que el webhook cree la fila en `subscriptions`. Con este flag la query
 * refetchea cada 2s mientras sea null, y se apaga sola al aparecer la sub.
 *
 * IMPORTANTE — race condition de auth: en Safari (y a veces Chrome con
 * cold start), la session de Supabase tarda unos ms en hidratarse desde
 * storage. Si la query corre antes, `auth.uid()` es null y RLS devuelve
 * vacío. React Query cachea ese null y nunca refetchea aunque la session
 * llegue después. Para evitarlo:
 *   - `enabled: !!user` → la query NO arranca hasta que tenemos user
 *   - El queryKey incluye `user.id` → cuando cambia el user (login, switch),
 *     la query se invalida automáticamente
 */
export function useMySubscription(opts?: { pollWhileMissing?: boolean }) {
  const user = useUser();
  return useQuery({
    // Incluir user.id en el key → cada user tiene su propia cache.
    // Si user es null/undefined, no llegamos a correr la query (enabled abajo).
    queryKey: ['my_subscription', user?.id],
    enabled: !!user,
    // Bug fix (2026-06-04): el filtro .eq('user_id', user.id) explícito es
    // OBLIGATORIO. Antes confiábamos en RLS para limitar a la fila del user,
    // pero la policy de admin permite leer TODAS las filas de subscriptions.
    // Para un admin sin sub propia, la query devolvía 3+ filas de otros users
    // → .maybeSingle() throwea PGRST116 → React Query reintenta → loop de
    // remount disparaba cientos de fetches/min hasta trabar la UI.
    // El filtro explícito hace la query determinística independientemente del
    // rol del user. Lo mismo aplicaba ya implícitamente para clientes via RLS.
    //
    // staleTime largo: la sub cambia rara vez (checkout, cancelación, renew
    // mensual). 5 min de cache son suficientes; los flujos que necesitan dato
    // fresco (post-checkout, post-cancel) ya hacen invalidateQueries explícito.
    // Además limita el blast radius si volviera a aparecer un loop de remount.
    staleTime: 5 * 60_000,
    queryFn: async () => {
      if (!user) return null;
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*, subscription_plans!inner(*)')
        .eq('user_id', user.id)
        .in('status', ['active', 'trialing', 'past_due'])
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    refetchInterval: (query) =>
      opts?.pollWhileMissing && !query.state.data ? 2000 : false,
  });
}

/**
 * Cambio de plan PENDIENTE (se aplica al fin del ciclo, no ahora).
 *
 * Cubre los dos casos en que el plan vigente sigue activo hasta una fecha y
 * luego cambia:
 *   - `cancel`    → cancelaste: al terminar el ciclo pasas a Free.
 *   - `downgrade` → bajaste de plan (ej. Premium → Pro) desde el portal de
 *     Stripe; el plan alto sigue vigente hasta el fin del ciclo.
 *
 * El upgrade NO aparece aquí porque es inmediato. Se deriva de los campos que
 * sincroniza el webhook de Stripe (`cancel_at_period_end`, `current_period_end`,
 * `scheduled_plan_code`, `scheduled_change_at`).
 */
export interface PendingPlanChange {
  kind: 'cancel' | 'downgrade';
  /** Familia de destino legible: 'Free' | 'En Vivo' | 'Premium'. */
  targetLabel: string;
  /** Fecha (ISO) en que aplica el cambio. */
  effectiveAt: string;
}

function planFamilyLabel(code: string): string {
  if (code.includes('premium')) return 'Premium';
  if (code.includes('pro')) return 'En Vivo';
  return 'Free';
}

export function getPendingPlanChange(
  sub:
    | {
        cancel_at_period_end?: boolean | null;
        current_period_end?: string | null;
        scheduled_plan_code?: string | null;
        scheduled_change_at?: string | null;
      }
    | null
    | undefined,
): PendingPlanChange | null {
  if (!sub) return null;
  // La cancelación tiene prioridad: el plan termina y se va a Free.
  if (sub.cancel_at_period_end && sub.current_period_end) {
    return { kind: 'cancel', targetLabel: 'Free', effectiveAt: sub.current_period_end };
  }
  if (sub.scheduled_plan_code && sub.scheduled_change_at) {
    return {
      kind: 'downgrade',
      targetLabel: planFamilyLabel(sub.scheduled_plan_code),
      effectiveAt: sub.scheduled_change_at,
    };
  }
  return null;
}

/**
 * Mutación: llamar a create-checkout edge function.
 *
 * Flujo Sprint 4.7:
 *   { family: 'pro' | 'premium' } → backend decide Founder vs Público según cupo
 *   { plan: 'pro_founder' | ... } → forzar plan específico (compatibilidad / VIP)
 *
 * Devuelve la URL de Stripe Checkout + qué plan se aplicó (para UX/telemetría).
 */
export function useCreateCheckout() {
  return useMutation({
    mutationFn: async (input: {
      family?: 'pro' | 'premium';
      plan?: SubscriptionPlan;
      success_url: string;
      cancel_url: string;
      /** Cupón de lanzamiento: el backend lo aplica en el checkout si existe. */
      promo_code?: string;
      /** Desde la página de un curso en vivo: de dónde llegó, el curso y la fecha
       *  (inscripción al volver y referido del guru; planes nuevos 2026-10-24). */
      ref?: string;
      live_course_id?: string;
      date_id?: string;
    }): Promise<{ url: string; applied_plan: SubscriptionPlan; applied_price_cents: number | null }> => {
      // Píxel de Meta: ?sid= para el event_id y las cookies _fbp/_fbc para la API de conversiones.
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { ...input, success_url: conSid(input.success_url), meta: metaCookies() },
      });
      // Caso: error de red o 401/403/500 sin body parseado
      if (error) {
        const ctx = (error as any).context;
        if (ctx && typeof ctx.json === 'function') {
          try {
            const body = await ctx.json();
            if (body?.code === 'no_seats_available') throw new Error('Cupo Founder agotado para este plan.');
            if (body?.code === 'invalid_input') throw new Error('Datos inválidos. Revisa el plan e intenta de nuevo.');
            if (body?.error) throw new Error(body.error);
          } catch (parseErr) {
            if (parseErr instanceof Error && parseErr.message !== 'Unauthorized') throw parseErr;
          }
        }
        const msg = error.message ?? '';
        if (msg.includes('401') || msg.toLowerCase().includes('unauthorized')) {
          throw new Error('Unauthorized: sesión expirada');
        }
        throw error;
      }
      if (data?.error) {
        const codeMap: Record<string, string> = {
          no_seats_available: 'Cupo Founder agotado para este plan.',
          invalid_input: 'Datos inválidos. Revisa el plan e intenta de nuevo.',
        };
        throw new Error(codeMap[data.code] ?? data.error);
      }
      if (!data?.data?.url) throw new Error('Respuesta inválida del checkout');
      return {
        url: data.data.url,
        applied_plan: data.data.applied_plan,
        applied_price_cents: data.data.applied_price_cents ?? null,
      };
    },
  });
}

/**
 * Mutación: llamar a customer-portal edge function.
 */
export function useCustomerPortal() {
  return useMutation({
    mutationFn: async (input: { return_url: string }): Promise<{ url: string }> => {
      const { data, error } = await supabase.functions.invoke('customer-portal', {
        body: input,
      });
      if (error) throw error;
      if (!data?.data?.url) throw new Error('Respuesta inválida del portal');
      return { url: data.data.url };
    },
  });
}
